import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle, Ban, Check, ChevronDown, Download, ExternalLink, FileSpreadsheet,
  Inbox, Instagram, Linkedin, Loader2, Monitor, Pencil, Plus, Trash2, Upload,
  Video, X, Youtube,
} from 'lucide-react';
import { api } from '../utils/api';
import { useIsMobile } from '../utils/useIsMobile';
import OrganicShell from '../components/organic/OrganicShell';

// Tradeshow — desktop-only. Upload an exhibitor spreadsheet, map its
// columns onto brand attributes, and let research augment every row
// (founders, HQ, funding, headcount, social accounts with follower
// counts, whether it is a beverage company at all). Each list is saved
// under a name, shown as a wide table and downloadable as CSV / Excel.
//
// The lists sit *outside* the brands pipeline on purpose: research here
// never creates a brand or a review row. The per-row "Add to pipeline"
// button is the only way in, and rows research marked as non-beverage
// keep their data but lose the button.

const STROKE = 2.75;
const POLL_MS = 3000;
const RUNNING = new Set(['pending', 'running']);
const SKIP = '';

function formatNumber(num) {
  if (num == null) return null;
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function hostOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

async function downloadResponse(response, fallbackName) {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || body.message || `Download failed (${response.status})`);
  }
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename=([^;]+)/);
  const filename = match ? match[1].trim().replace(/^"|"$/g, '') : fallbackName;
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

// ── Upload + column mapping dialog ───────────────────────────────────

function MappingDialog({ onClose, onStarted }) {
  const [phase, setPhase] = useState('select'); // select | uploading | map | starting
  const [file, setFile] = useState(null);
  const [draft, setDraft] = useState(null);      // the upload response
  const [mapping, setMapping] = useState({});
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const draftRef = useRef(null);

  useEffect(() => { draftRef.current = draft; }, [draft]);

  // Closing after the upload but before the start cancels the draft so
  // half-mapped files don't pile up server-side.
  const cancel = useCallback(async () => {
    const current = draftRef.current;
    if (current && phase !== 'starting') {
      try { await api.deleteTradeshowList(current.list_id); } catch { /* best effort */ }
    }
    onClose();
  }, [onClose, phase]);

  const upload = async () => {
    if (!file) return;
    setPhase('uploading');
    setError(null);
    try {
      const result = await api.uploadTradeshowSpreadsheet(file);
      setDraft(result);
      setMapping(result.suggested_mapping || {});
      setName(result.name || '');
      setPhase('map');
    } catch (err) {
      setError(err.message);
      setPhase('select');
    }
  };

  const fields = draft?.fields || [];
  const usedBy = useMemo(() => {
    const out = {};
    Object.entries(mapping).forEach(([column, key]) => { if (key) out[key] = column; });
    return out;
  }, [mapping]);
  const nameMapped = Boolean(usedBy.name);
  const mappedCount = Object.values(mapping).filter(Boolean).length;

  const setColumn = (column, key) => {
    setMapping((prev) => {
      const next = { ...prev, [column]: key || SKIP };
      // One column per attribute: picking an attribute already used
      // elsewhere moves it here instead of failing on submit.
      if (key) {
        Object.keys(next).forEach((other) => {
          if (other !== column && next[other] === key) next[other] = SKIP;
        });
      }
      return next;
    });
  };

  const start = async () => {
    if (!draft || !nameMapped || !name.trim()) return;
    setPhase('starting');
    setError(null);
    try {
      const clean = {};
      Object.entries(mapping).forEach(([column, key]) => { if (key) clean[column] = key; });
      const result = await api.startTradeshowList(draft.list_id, name.trim(), clean);
      onStarted(result.list);
    } catch (err) {
      setError(err.message);
      setPhase('map');
    }
  };

  const samples = draft?.sample_rows || [];

  return (
    <div className="dialog-backdrop" onClick={cancel} role="presentation">
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tradeshow-dialog-title"
        style={{ width: 'min(880px, 100%)', maxHeight: '90vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
          <h3 id="tradeshow-dialog-title" className="dialog-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileSpreadsheet size={20} strokeWidth={STROKE} />
            {phase === 'map' || phase === 'starting' ? 'Map the columns' : 'Upload a tradeshow list'}
          </h3>
          <button type="button" className="btn btn-icon btn-secondary" title="Close" aria-label="Close" onClick={cancel}>
            <X size={16} strokeWidth={STROKE} />
          </button>
        </div>

        {error && (
          <div className="card" role="alert" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, background: 'var(--color-accent-100)', border: '1px solid var(--color-accent)', padding: 'var(--space-2) var(--space-3)', fontSize: 13 }}>
            <AlertCircle size={16} strokeWidth={STROKE} /> {error}
          </div>
        )}

        {(phase === 'select' || phase === 'uploading') && (
          <>
            <p className="dialog-body" style={{ margin: 0 }}>
              A CSV or Excel (.xlsx) file with one exhibitor per row and a header row.
              Next you pick which columns hold the brand name, website, socials and so on
              — the rest are kept as they are and come back in the download.
              Up to 300 rows per list; each row costs one research credit.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.tsv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setError(null); }}
              disabled={phase === 'uploading'}
            />
            {file && (
              <div style={{ fontSize: 13, opacity: 0.75 }}>
                Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
              </div>
            )}
            <div className="dialog-actions">
              <button type="button" className="btn btn-secondary" onClick={cancel}>Cancel</button>
              <button type="button" className="btn btn-primary" disabled={!file || phase === 'uploading'} onClick={upload}>
                {phase === 'uploading'
                  ? <><Loader2 size={14} strokeWidth={STROKE} className="spin" /> Reading file…</>
                  : <><Upload size={14} strokeWidth={STROKE} /> Continue</>}
              </button>
            </div>
          </>
        )}

        {(phase === 'map' || phase === 'starting') && draft && (
          <>
            <p className="dialog-body" style={{ margin: 0 }}>
              <strong>{draft.filename}</strong> · {draft.total_rows} row{draft.total_rows === 1 ? '' : 's'} ·
              {' '}{draft.columns.length} columns. Pick the attribute each column holds, or leave it as
              <em> Skip</em> — skipped columns are kept in the download, just not researched.
              The brand / company name is required.
            </p>
            {draft.warnings?.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--color-accent-800)' }}>
                {draft.warnings.map((w) => <div key={w}>{w}</div>)}
              </div>
            )}

            <div className="field">
              <label htmlFor="tradeshow-list-name">List name</label>
              <input
                id="tradeshow-list-name"
                className="input"
                value={name}
                maxLength={255}
                placeholder="Expo West 2026"
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ width: '28%' }}>Spreadsheet column</th>
                    <th>First values</th>
                    <th style={{ width: '32%' }}>Maps to</th>
                  </tr>
                </thead>
                <tbody>
                  {draft.columns.map((column) => {
                    const key = mapping[column] || SKIP;
                    const preview = samples
                      .map((row) => row[column])
                      .filter(Boolean)
                      .slice(0, 3)
                      .join(' · ');
                    return (
                      <tr key={column}>
                        <td style={{ fontWeight: 600 }}>{column}</td>
                        <td style={{ opacity: 0.7, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={preview}>
                          {preview || <span style={{ opacity: 0.5 }}>empty</span>}
                        </td>
                        <td>
                          <select
                            className="input"
                            aria-label={`Attribute for column ${column}`}
                            value={key}
                            style={key ? { borderColor: 'var(--color-accent)' } : undefined}
                            onChange={(e) => setColumn(column, e.target.value)}
                          >
                            <option value={SKIP}>Skip</option>
                            {fields.map((f) => (
                              <option key={f.key} value={f.key}>
                                {f.label}{f.required ? ' (required)' : ''}
                                {usedBy[f.key] && usedBy[f.key] !== column ? ` — currently "${usedBy[f.key]}"` : ''}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="dialog-actions" style={{ alignItems: 'center' }}>
              <span style={{ marginRight: 'auto', fontSize: 12, opacity: 0.7 }}>
                {nameMapped
                  ? `${mappedCount} column${mappedCount === 1 ? '' : 's'} mapped · ${draft.columns.length - mappedCount} skipped`
                  : 'Map one column to the brand / company name to continue'}
              </span>
              <button type="button" className="btn btn-secondary" onClick={cancel} disabled={phase === 'starting'}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!nameMapped || !name.trim() || phase === 'starting'}
                onClick={start}
              >
                {phase === 'starting'
                  ? <><Loader2 size={14} strokeWidth={STROKE} className="spin" /> Starting…</>
                  : <><Check size={14} strokeWidth={STROKE} /> Research {draft.total_rows} brand{draft.total_rows === 1 ? '' : 's'}</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Row cells ────────────────────────────────────────────────────────

function SocialCell({ url, followers, Icon, label }) {
  if (!url) return <span style={{ opacity: 0.4 }}>—</span>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title={`${label} profile`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none', whiteSpace: 'nowrap' }}>
      <Icon size={13} strokeWidth={STROKE} />
      <span style={{ fontFamily: 'var(--font-heading)', fontSize: 14 }}>
        {followers != null ? formatNumber(followers) : 'open'}
      </span>
    </a>
  );
}

function BeverageBadge({ value }) {
  if (value === true) return <span className="tag tag-accent-2">Beverage</span>;
  if (value === false) return <span className="tag tag-neutral">Not beverage</span>;
  return <span className="tag tag-outline" style={{ opacity: 0.7 }}>Unknown</span>;
}

function StatusChip({ status }) {
  switch (status) {
    case 'completed':
      return null;
    case 'running':
      return <span className="tag tag-accent" style={{ gap: 4 }}><Loader2 size={11} className="spin" /> Researching</span>;
    case 'pending':
      return <span className="tag tag-neutral">Queued</span>;
    case 'credits_denied':
      return <span className="tag tag-accent" title="The org's credit balance refused this row">Credits denied</span>;
    case 'no_data':
      return <span className="tag tag-neutral">Nothing found</span>;
    default:
      return <span className="tag tag-accent">Failed</span>;
  }
}

function PipelineCell({ row, busy, onAdd }) {
  if (row.matched_brand_id) {
    return (
      <Link to={`/brands/${row.matched_brand_id}`} className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 12px', whiteSpace: 'nowrap' }} title="Already tracked — open the brand page">
        <ExternalLink size={12} strokeWidth={STROKE} /> Tracked
      </Link>
    );
  }
  if (row.pending_brand_id) {
    return (
      <Link to="/brands/research/pending" className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 8px', whiteSpace: 'nowrap' }} title="Waiting in the Review queue">
        <Inbox size={12} strokeWidth={STROKE} /> In review
      </Link>
    );
  }
  if (row.is_beverage === false) {
    return (
      <span
        className="btn btn-secondary"
        aria-disabled="true"
        style={{ fontSize: 12, padding: '5px 12px', opacity: 0.45, cursor: 'not-allowed', whiteSpace: 'nowrap' }}
        title={`Not a beverage brand${row.industry ? ` (${row.industry})` : ''} — can't be added to the pipeline`}
      >
        <Ban size={12} strokeWidth={STROKE} /> Not eligible
      </span>
    );
  }
  if (RUNNING.has(row.research_status)) {
    return <span style={{ fontSize: 12, opacity: 0.5 }}>…</span>;
  }
  return (
    <button
      type="button"
      className="btn btn-primary"
      style={{ fontSize: 12, padding: '5px 12px', whiteSpace: 'nowrap' }}
      disabled={busy}
      title="Queue this brand in the Review inbox"
      onClick={() => onAdd(row)}
    >
      {busy ? <Loader2 size={12} strokeWidth={STROKE} className="spin" /> : <Plus size={12} strokeWidth={STROKE} />} Add to pipeline
    </button>
  );
}

function FoundersCell({ row }) {
  const profiles = row.founder_linkedin_urls || [];
  if (!row.founders) return <span style={{ opacity: 0.4 }}>—</span>;
  if (profiles.length === 0) return row.founders;
  return (
    <span>
      {profiles.map((p, i) => (
        <React.Fragment key={`${p.name}-${i}`}>
          {i > 0 && ', '}
          {p.linkedin_url
            ? <a href={p.linkedin_url} target="_blank" rel="noopener noreferrer" title="LinkedIn profile">{p.name}</a>
            : p.name}
        </React.Fragment>
      ))}
    </span>
  );
}

function Muted({ children }) {
  return children ? <>{children}</> : <span style={{ opacity: 0.4 }}>—</span>;
}

// ── Page ─────────────────────────────────────────────────────────────

function Tradeshow() {
  const isMobile = useIsMobile();
  const [lists, setLists] = useState([]);
  const [listsLoading, setListsLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);  // { list, rows }
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [busyRow, setBusyRow] = useState(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [filter, setFilter] = useState('all'); // all | beverage | not_in_pipeline
  const pollRef = useRef(null);

  const loadLists = useCallback(async () => {
    try {
      const data = await api.listTradeshowLists();
      setLists(data.lists || []);
      return data.lists || [];
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setListsLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (listId, { quiet = false } = {}) => {
    if (!listId) return;
    if (!quiet) setDetailLoading(true);
    try {
      const data = await api.getTradeshowList(listId);
      setDetail(data);
      setLists((prev) => prev.map((l) => (l.id === data.list.id ? data.list : l)));
    } catch (err) {
      if (err.status === 404) {
        setSelectedId(null);
        setDetail(null);
      } else {
        setError(err.message);
      }
    } finally {
      if (!quiet) setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLists().then((initial) => {
      if (initial.length > 0) setSelectedId((prev) => prev ?? initial[0].id);
    });
  }, [loadLists]);

  useEffect(() => {
    setDownloadOpen(false);
    setRenaming(false);
    setFilter('all');
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  // Poll while research runs; stop the moment the list settles.
  const status = detail?.list?.status;
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    if (selectedId && RUNNING.has(status)) {
      pollRef.current = setInterval(() => loadDetail(selectedId, { quiet: true }), POLL_MS);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedId, status, loadDetail]);

  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 5000);
    return () => clearTimeout(t);
  }, [flash]);

  const handleStarted = (list) => {
    setDialogOpen(false);
    setLists((prev) => [list, ...prev]);
    setSelectedId(list.id);
    setFlash(`Researching ${list.total_rows} brand${list.total_rows === 1 ? '' : 's'} for "${list.name}" — the table fills in as rows finish.`);
  };

  const handleDelete = async (list) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${list.name}"? Brands you already added to the pipeline stay there.`)) return;
    try {
      await api.deleteTradeshowList(list.id);
      const remaining = lists.filter((l) => l.id !== list.id);
      setLists(remaining);
      if (selectedId === list.id) setSelectedId(remaining[0]?.id ?? null);
    } catch (err) {
      setError(err.message);
    }
  };

  const submitRename = async () => {
    const name = renameValue.trim();
    if (!name || !detail) { setRenaming(false); return; }
    try {
      const result = await api.renameTradeshowList(detail.list.id, name);
      setDetail((prev) => ({ ...prev, list: result.list }));
      setLists((prev) => prev.map((l) => (l.id === result.list.id ? result.list : l)));
    } catch (err) {
      setError(err.message);
    } finally {
      setRenaming(false);
    }
  };

  const handleAdd = async (row) => {
    setBusyRow(row.id);
    setError(null);
    try {
      const result = await api.addTradeshowRowToPipeline(detail.list.id, row.id);
      setDetail((prev) => ({
        ...prev,
        rows: prev.rows.map((r) => (r.id === result.row.id ? result.row : r)),
      }));
      setFlash(result.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyRow(null);
    }
  };

  const handleDownload = async (format) => {
    if (!detail) return;
    setDownloadOpen(false);
    setDownloading(true);
    setError(null);
    try {
      const response = await api.exportTradeshowList(detail.list.id, format);
      await downloadResponse(response, `${detail.list.name}.${format}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const rows = useMemo(() => detail?.rows || [], [detail]);
  const visibleRows = useMemo(() => {
    if (filter === 'beverage') return rows.filter((r) => r.is_beverage === true);
    if (filter === 'not_in_pipeline') {
      return rows.filter((r) => !r.matched_brand_id && !r.pending_brand_id && r.is_beverage !== false);
    }
    return rows;
  }, [rows, filter]);

  const counts = useMemo(() => ({
    beverage: rows.filter((r) => r.is_beverage === true).length,
    tracked: rows.filter((r) => r.matched_brand_id).length,
    review: rows.filter((r) => r.pending_brand_id).length,
  }), [rows]);

  if (isMobile) {
    return (
      <OrganicShell active="tradeshow">
        <h1 className="o-page-title">Tradeshow</h1>
        <div className="card" style={{ alignItems: 'flex-start', gap: 'var(--space-2)' }}>
          <Monitor size={22} strokeWidth={STROKE} />
          <strong>Tradeshow lists are a desktop tool.</strong>
          <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
            Uploading a spreadsheet and reviewing the wide research table needs a bigger
            screen. Open Lead Discovery on a computer to use it.
          </p>
          <Link to="/brands" className="btn btn-secondary">Back to Brands</Link>
        </div>
      </OrganicShell>
    );
  }

  const list = detail?.list;
  const running = list && RUNNING.has(list.status);
  const progress = list && list.total_rows ? Math.round((list.processed_rows / list.total_rows) * 100) : 0;

  return (
    <OrganicShell active="tradeshow" contentStyle={{ maxWidth: 'none', padding: 'var(--space-8)' }}>
      <header style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 'var(--space-4) var(--space-6)', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 300px' }}>
          <h1 className="o-page-title">Tradeshow</h1>
          <p className="o-page-subhead">
            Upload an exhibitor list, map its columns, and get every brand researched — socials
            with follower counts, founders, HQ, funding, and whether it is already in your pipeline.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setDialogOpen(true)}>
          <Upload size={16} strokeWidth={STROKE} /> Upload spreadsheet
        </button>
      </header>

      {error && (
        <div className="card" role="alert" style={{ flexDirection: 'row', alignItems: 'center', gap: 'var(--space-2)', background: 'var(--color-accent-100)', border: '1px solid var(--color-accent)', padding: 'var(--space-2) var(--space-4)', fontSize: 14 }}>
          <AlertCircle size={16} strokeWidth={STROKE} />
          <span style={{ flex: 1 }}>{error}</span>
          <button type="button" className="btn btn-icon btn-ghost" aria-label="Dismiss" onClick={() => setError(null)}><X size={14} strokeWidth={STROKE} /></button>
        </div>
      )}
      {flash && (
        <div className="card" role="status" style={{ flexDirection: 'row', alignItems: 'center', gap: 'var(--space-2)', background: 'var(--color-accent-2-100)', padding: 'var(--space-2) var(--space-4)', fontSize: 14 }}>
          <Check size={16} strokeWidth={STROKE} /> {flash}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '260px minmax(0, 1fr)', gap: 'var(--space-4)', alignItems: 'start' }}>
        {/* Saved lists */}
        <aside className="card" style={{ gap: 'var(--space-2)', position: 'sticky', top: 'var(--space-4)' }}>
          <span className="card-kicker">Your lists</span>
          {listsLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, opacity: 0.7 }}>
              <Loader2 size={14} strokeWidth={STROKE} className="spin" /> Loading…
            </div>
          ) : lists.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, opacity: 0.7 }}>
              No lists yet. Upload a spreadsheet to create the first one.
            </p>
          ) : (
            lists.map((l) => {
              const active = l.id === selectedId;
              return (
                <div
                  key={l.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    borderRadius: 'var(--radius-md)',
                    background: active ? 'var(--color-accent-100)' : 'transparent',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setSelectedId(l.id)}
                    aria-current={active ? 'true' : undefined}
                    style={{
                      flex: 1, minWidth: 0, textAlign: 'left', cursor: 'pointer',
                      background: 'none', border: 'none', padding: '8px 10px',
                      font: 'inherit', color: 'inherit', borderRadius: 'var(--radius-md)',
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</div>
                    <div style={{ fontSize: 11, opacity: 0.65, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {RUNNING.has(l.status)
                        ? <><Loader2 size={11} className="spin" /> {l.processed_rows}/{l.total_rows}</>
                        : `${l.total_rows} brand${l.total_rows === 1 ? '' : 's'}`}
                      <span>·</span>
                      {formatDate(l.created_at)}
                    </div>
                  </button>
                  <button
                    type="button"
                    className="btn btn-icon btn-ghost"
                    style={{ width: 30, height: 30, flex: 'none' }}
                    title="Delete list"
                    aria-label={`Delete ${l.name}`}
                    onClick={() => handleDelete(l)}
                  >
                    <Trash2 size={13} strokeWidth={STROKE} />
                  </button>
                </div>
              );
            })
          )}
        </aside>

        {/* The augmented table */}
        <section className="card" style={{ gap: 'var(--space-3)', minWidth: 0 }}>
          {!list ? (
            <div style={{ padding: 'var(--space-6)', textAlign: 'center', opacity: 0.7, fontSize: 14 }}>
              {detailLoading
                ? <><Loader2 size={16} strokeWidth={STROKE} className="spin" /> Loading…</>
                : 'Pick a list on the left, or upload a spreadsheet to start one.'}
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0, flex: '1 1 320px' }}>
                  {renaming ? (
                    <form onSubmit={(e) => { e.preventDefault(); submitRename(); }} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input className="input" value={renameValue} maxLength={255} autoFocus onChange={(e) => setRenameValue(e.target.value)} aria-label="List name" style={{ maxWidth: 360 }} />
                      <button type="submit" className="btn btn-primary" style={{ fontSize: 12 }}>Save</button>
                      <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => setRenaming(false)}>Cancel</button>
                    </form>
                  ) : (
                    <h3 className="o-card-head-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{list.name}</span>
                      <button type="button" className="btn btn-icon btn-ghost" style={{ width: 30, height: 30 }} title="Rename list" aria-label="Rename list" onClick={() => { setRenameValue(list.name); setRenaming(true); }}>
                        <Pencil size={13} strokeWidth={STROKE} />
                      </button>
                    </h3>
                  )}
                  <p className="o-card-head-sub">
                    {list.filename ? `${list.filename} · ` : ''}
                    {list.total_rows} brand{list.total_rows === 1 ? '' : 's'}
                    {list.status === 'completed' && ` · ${counts.beverage} beverage · ${counts.tracked} tracked · ${counts.review} in review`}
                    {list.status === 'failed' && ` · failed: ${list.error_message || 'unknown error'}`}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', position: 'relative', flex: 'none' }}>
                  <div className="seg" role="radiogroup" aria-label="Filter rows">
                    {[['all', 'All'], ['beverage', 'Beverage'], ['not_in_pipeline', 'Not in pipeline']].map(([value, label]) => (
                      <label key={value} className="seg-opt">
                        <input type="radio" name="tradeshow-filter" value={value} checked={filter === value} onChange={() => setFilter(value)} />
                        {label}
                      </label>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={downloading || rows.length === 0}
                    aria-expanded={downloadOpen}
                    onClick={() => setDownloadOpen((v) => !v)}
                  >
                    {downloading ? <Loader2 size={14} strokeWidth={STROKE} className="spin" /> : <Download size={14} strokeWidth={STROKE} />}
                    {' '}Download <ChevronDown size={12} strokeWidth={STROKE} />
                  </button>
                  {downloadOpen && (
                    <div className="card elev-lg" style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 50, padding: 'var(--space-2)', minWidth: 190 }}>
                      <button type="button" className="o-account-menu-item" onClick={() => handleDownload('xlsx')}>
                        <FileSpreadsheet size={14} strokeWidth={STROKE} /> Excel (.xlsx)
                      </button>
                      <button type="button" className="o-account-menu-item" onClick={() => handleDownload('csv')}>
                        <FileSpreadsheet size={14} strokeWidth={STROKE} /> CSV
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {running && (
                <div role="status" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <Loader2 size={14} strokeWidth={STROKE} className="spin" />
                    Researching… {list.processed_rows} of {list.total_rows} done. You can leave this page; the list keeps filling in.
                  </div>
                  <div style={{ height: 6, borderRadius: 999, background: 'var(--color-neutral-300)', overflow: 'hidden' }}>
                    <div style={{ width: `${progress}%`, height: '100%', background: 'var(--color-accent)', transition: 'width 300ms' }} />
                  </div>
                </div>
              )}

              <div style={{ overflowX: 'auto', margin: '0 calc(-1 * var(--space-3))', padding: '0 var(--space-3)' }}>
                <table className="table o-ts-table" style={{ fontSize: 13, minWidth: 1500 }}>
                  <thead>
                    <tr>
                      <th style={{ minWidth: 210 }}>Brand</th>
                      <th style={{ minWidth: 150 }}>Website</th>
                      <th>Instagram</th>
                      <th>TikTok</th>
                      <th>LinkedIn</th>
                      <th style={{ minWidth: 170 }}>Founders</th>
                      <th style={{ minWidth: 150 }}>HQ</th>
                      <th>Founded</th>
                      <th style={{ minWidth: 120 }}>Parent</th>
                      <th style={{ minWidth: 140 }}>Funding</th>
                      <th>Employees</th>
                      <th style={{ minWidth: 130 }}>Retail</th>
                      <th style={{ minWidth: 260 }}>About &amp; recent news</th>
                      <th style={{ minWidth: 150 }}>Pipeline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => {
                      const hq = [row.hq_city, row.hq_state, row.country].filter(Boolean).join(', ');
                      return (
                        <tr key={row.id}>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <span style={{ fontWeight: 600, fontSize: 14 }}>{row.name}</span>
                              <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                                <BeverageBadge value={row.is_beverage} />
                                <StatusChip status={row.research_status} />
                              </span>
                              {(row.industry || row.category) && (
                                <span style={{ fontSize: 12, opacity: 0.7 }}>{row.industry || row.category}</span>
                              )}
                              {row.research_status !== 'completed' && row.research_message && (
                                <span style={{ fontSize: 11, opacity: 0.6 }}>{row.research_message}</span>
                              )}
                            </div>
                          </td>
                          <td>
                            {row.website
                              ? <a href={row.website} target="_blank" rel="noopener noreferrer" style={{ overflowWrap: 'anywhere' }}>{hostOf(row.website)}</a>
                              : <Muted />}
                          </td>
                          <td><SocialCell url={row.instagram_url} followers={row.instagram_followers} Icon={Instagram} label="Instagram" /></td>
                          <td><SocialCell url={row.tiktok_url} followers={row.tiktok_followers} Icon={Video} label="TikTok" /></td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <SocialCell url={row.linkedin_url} followers={row.linkedin_followers} Icon={Linkedin} label="LinkedIn" />
                              {row.youtube_url && (
                                <a href={row.youtube_url} target="_blank" rel="noopener noreferrer" title="YouTube channel" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, textDecoration: 'none' }}>
                                  <Youtube size={12} strokeWidth={STROKE} /> YouTube
                                </a>
                              )}
                            </div>
                          </td>
                          <td><FoundersCell row={row} /></td>
                          <td><Muted>{hq}</Muted></td>
                          <td><Muted>{row.founded_year}</Muted></td>
                          <td><Muted>{row.parent_company}</Muted></td>
                          <td><Muted>{row.funding}</Muted></td>
                          <td><Muted>{row.employee_range}</Muted></td>
                          <td><Muted>{row.marketplaces?.length ? row.marketplaces.join(', ') : null}</Muted></td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 360 }}>
                              {row.description && <span style={{ fontSize: 12.5 }}>{row.description}</span>}
                              {row.recent_news && (
                                <span style={{ fontSize: 12, color: 'var(--color-accent-800)' }}>{row.recent_news}</span>
                              )}
                              {!row.description && !row.recent_news && <Muted />}
                            </div>
                          </td>
                          <td><PipelineCell row={row} busy={busyRow === row.id} onAdd={handleAdd} /></td>
                        </tr>
                      );
                    })}
                    {visibleRows.length === 0 && (
                      <tr>
                        <td colSpan={14} style={{ textAlign: 'center', opacity: 0.65, padding: 'var(--space-4)' }}>
                          {rows.length === 0 ? 'No rows in this list.' : 'No rows match this filter.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p style={{ margin: 0, fontSize: 12, opacity: 0.6 }}>
                Values you mapped from the spreadsheet are kept as-is; research only fills the blanks.
                "Add to pipeline" queues the brand in the Review inbox, where approving it runs the
                full deep research. Rows research marked as not a beverage company can be downloaded
                but not added.
              </p>
            </>
          )}
        </section>
      </div>

      {dialogOpen && <MappingDialog onClose={() => setDialogOpen(false)} onStarted={handleStarted} />}
    </OrganicShell>
  );
}

export default Tradeshow;
